import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid.ts";
import { Divider, Popover, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  ConditionConfiguration,
  ConditionFilterDescriptor,
  FieldConfiguration,
} from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import { ConditionEqualityType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField, DimensionDescriptor, ReportField } from "../../../../../../../../api/types/svTypes.ts";
import { AreaItemType, ItemDataType } from "../../../../../../../../api/types/svTypes.ts";
import { useBiCommonDataContext } from "../../../../contexts/BiCommonDataContext.ts";
import { validateCondition } from "../../../../store/utils/fieldsUtils.ts";
import { getDimensionsAllowedInConditions, isAllowedInConditions } from "../../../../utilities/conditionDataTypes.ts";
import type { ShowFieldOptionsSettings } from "../Types.ts";
import ConditionFieldOptionPopup from "../conditions/ConditionFieldOptionPopup.tsx";
import { resetMandatoryField } from "../conditions/conditionsHelper.tsx";
import { getFiltersToApply } from "../hooks/getFiltersToApply.ts";
import AddCustomConditions from "./AddCustomConditions.tsx";
import LinkConditions from "./LinkConditions.tsx";
import MeasureItemConditions from "./MeasureItemConditions.tsx";
import { getMeasureCaption } from "./helper.tsx";

interface Props {
  field: ReportField;
  dimensions: DimensionDescriptor[];
  globalConditions: ConditionField[];
  canToggleParameter?: boolean;
  canBeRemoved: boolean;
  isReadonlyCustomLabel: boolean;
  autoShowItemOptionsId?: string;
  updateField: ((field: ReportField, change: Partial<FieldConfiguration>) => void) | undefined;
  onSetAutoShowItemOptionsId: ((id?: string) => void) | undefined;
  readonly: boolean;
  detailedView?: boolean;
  popupAnchorEl?: HTMLElement;
  onClosePopup?: () => void;
}
const CustomConditions = ({
  field,
  dimensions,
  globalConditions,
  canBeRemoved,
  canToggleParameter,
  isReadonlyCustomLabel,
  updateField,
  autoShowItemOptionsId,
  onSetAutoShowItemOptionsId,
  readonly,
  detailedView,
  popupAnchorEl,
  onClosePopup,
}: Props) => {
  const { isApiReport, reportType } = useBiCommonDataContext();
  const [fieldOptions, setFieldOptions] = useState<ShowFieldOptionsSettings<ConditionField>>();
  const [showMeasureOptions, setShowMeasureOptions] = useState(false);

  const [conditions, setConditions] = useState<ConditionField[]>([]);
  const [linkedConditions, setLinkedConditions] = useState<ConditionField[]>([]);
  const currentCondition = useMemo(() => {
    if (fieldOptions === undefined) return undefined;
    return conditions.find((c) => c.meta.name === fieldOptions.field.meta.name);
  }, [conditions, fieldOptions]);
  const narrowingFilters = useMemo(
    () => (currentCondition === undefined ? [] : getFiltersToApply(conditions, currentCondition)),
    [conditions, currentCondition]
  );

  //update linked conditions when global condition has changed
  useEffect(() => {
    let customConditions: ConditionField[] = [];
    if (field.config.customConditions) {
      customConditions = field.config.customConditions
        .map((cc) => {
          const f = dimensions.find((fld) => fld.name === cc.filter.dimensionName);
          if (f) {
            const copied: ConditionField = {
              config: cc,
              meta: f,
              areaItemType: AreaItemType.VALUES,
            };
            copied.validation = validateCondition(copied, [copied], reportType);
            return copied;
          }
          return undefined;
        })
        .filter((item): item is ConditionField => !!item);
    }
    // TODO #209
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    return setConditions(customConditions);
  }, [field.config.customConditions, setConditions, dimensions, reportType]);

  //update linked conditions when global condition has changed
  useEffect(() => {
    let linkedConditions: ConditionField[] = [];
    if (field.config.linkedConditions) {
      linkedConditions = field.config.linkedConditions
        .map((guid) => {
          const condition = globalConditions.find((gc) => gc.config.guid === guid);
          if (condition) {
            const copied = { ...structuredClone(condition), linked: true } as ConditionField;
            return copied;
          }
          return undefined;
        })
        .filter((item): item is ConditionField => !!item);
    }

    // TODO #209
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    return setLinkedConditions(linkedConditions);
  }, [field.config.linkedConditions, globalConditions, setLinkedConditions]);

  const fieldsForConditions = useMemo(() => {
    return getDimensionsAllowedInConditions(dimensions, reportType).filter(
      (f) => !conditions.some((c) => c.meta.name === f.name)
    );
  }, [dimensions, conditions, reportType]);

  const addCondition = useCallback(
    (value: DimensionDescriptor) => {
      const newCondition: ConditionConfiguration = {
        guid: generateGuid(),
        filter: { dimensionName: value.name, values: [], equalityType: ConditionEqualityType.Equal },
      };

      updateField?.(field, {
        useCustomConditions: true,
        customConditions: [...(field.config.customConditions ?? []), newCondition],
      });
      onSetAutoShowItemOptionsId?.(newCondition.guid);
    },
    [field, updateField, onSetAutoShowItemOptionsId]
  );

  const handleSetLinkCondition = useCallback(
    (condition: ConditionField) => {
      const newLinkedConditions = [...linkedConditions, { ...condition, linked: true }];
      setLinkedConditions(newLinkedConditions);
      updateField?.(field, {
        useCustomConditions: true,
        linkedConditions: newLinkedConditions.map((v) => v.config.guid),
      });
    },
    [field, updateField, linkedConditions]
  );

  const onRemoveConditionItem = useCallback(
    (item: ConditionField) => {
      if (!field.config.customConditions) return;
      const conditionItem = field.config.customConditions.find((c) => c.filter.dimensionName === item.meta.name);
      if (conditionItem !== undefined) {
        const copied = [...field.config.customConditions];
        const index = copied.indexOf(conditionItem);
        copied.splice(index, 1);
        updateField?.(field, {
          useCustomConditions: copied.length > 0 || !!field.config.linkedConditions?.length,
          customConditions: copied,
        });
      }
    },
    [field, updateField]
  );

  const onRemoveLinkedConditionItem = useCallback(
    (item: ConditionField) => {
      if (!field.config.linkedConditions) return;
      const index = field.config.linkedConditions.findIndex((c) => c === item.config.guid);
      if (index > -1) {
        const copied = [...field.config.linkedConditions];
        copied.splice(index, 1);
        updateField?.(field, {
          useCustomConditions: copied.length > 0 || !!field.config.customConditions?.length,
          linkedConditions: copied,
        });
      }
    },
    [field, updateField]
  );

  const onUpdateAsParameter = useCallback(
    (field: ConditionField, newValue: boolean) => {
      const copy = structuredClone(conditions);
      const condition = copy.find((c) => c.config.guid === field.config.guid);
      if (condition !== undefined) {
        const changes = resetMandatoryField(condition.config, { parameter: newValue });
        condition.config = { ...condition.config, ...changes };
        setConditions(copy);
      }
    },
    [conditions]
  );

  const onUpdateAsMandatory = useCallback(
    (field: ConditionField, newValue: boolean) => {
      const copy = structuredClone(conditions);
      const condition = copy.find((c) => c.config.guid === field.config.guid);
      if (condition !== undefined) {
        condition.config.mandatory = newValue;
        setConditions(copy);
      }
    },
    [conditions]
  );

  const updateCustomConditionProperty = useCallback(
    <K extends keyof ConditionConfiguration>(
      condition: ConditionField,
      propName: K,
      value: ConditionConfiguration[K]
    ) => {
      if (!field.config.customConditions) return;
      const copy = structuredClone(field.config.customConditions);
      const customConditionItem = copy.find((c) => c.filter.dimensionName === condition.meta.name);
      if (customConditionItem) {
        customConditionItem[propName] = value;
        return copy;
      }
      return;
    },
    [field]
  );

  const updateCustomConditionFilterAndParameter = useCallback(
    (current: ConditionField, condition: Partial<ConditionFilterDescriptor>) => {
      if (!field.config.customConditions) return;

      const copy: ConditionConfiguration[] | undefined = structuredClone(field.config.customConditions);
      const customConditionItem = copy.find((cc) => cc.guid === current.config.guid);
      if (
        customConditionItem &&
        (customConditionItem.parameter !== current.config.parameter ||
          customConditionItem.mandatory !== current.config.mandatory)
      ) {
        customConditionItem.parameter = current.config.parameter;
        customConditionItem.mandatory = current.config.mandatory;
      }
      if (customConditionItem) {
        customConditionItem.filter = { ...customConditionItem.filter, ...condition };
        updateField?.(field, { customConditions: copy });
      }
    },
    [field, updateField]
  );

  const onUpdateCustomLabel = useCallback(
    (condition: ConditionField, customLabel: string | undefined) => {
      const newConditions = updateCustomConditionProperty(condition, "customLabel", customLabel);
      if (newConditions !== undefined) {
        updateField?.(field, { customConditions: newConditions });
      }
    },
    [field, updateField, updateCustomConditionProperty]
  );

  const onShowMeasureOptions = useCallback(
    (settings: ShowFieldOptionsSettings<ConditionField>) => {
      setFieldOptions(settings);
      setShowMeasureOptions(true);
    },
    [setFieldOptions, setShowMeasureOptions]
  );

  const conditionsToShow = useMemo(() => {
    if (canBeRemoved) {
      return conditions;
    }
    return conditions.filter((c) => c.config.parameter === true && !c.validation?.invalid);
  }, [conditions, canBeRemoved]);

  const linkedConditionsToShow = useMemo(() => {
    if (canBeRemoved) {
      return linkedConditions;
    }
    return linkedConditions.filter((c) => c.config.parameter === true && !c.validation?.invalid);
  }, [linkedConditions, canBeRemoved]);

  const linkableConditions = useMemo(() => {
    return globalConditions.filter(
      (c) =>
        isAllowedInConditions(c.meta, reportType) && !linkedConditions.some((lc) => lc.config.guid === c.config.guid)
    );
  }, [globalConditions, linkedConditions, reportType]);

  const conditionRowsProps = {
    conditions: [...conditionsToShow, ...linkedConditionsToShow],
    canBeRemoved: canBeRemoved && !readonly,
    onRemoveItem: (condition: ConditionField) => {
      if (condition.linked) {
        onRemoveLinkedConditionItem(condition);
      } else {
        onRemoveConditionItem(condition);
      }
    },
    onShowOptions: onShowMeasureOptions,
    disableEqualitySelection: true,
    onSetAutoShowItemOptionsId,
    readonly,
  };
  const showInlineConditions = (detailedView ?? true) && conditionRowsProps.conditions.length > 0;

  return (
    <>
      {(detailedView !== undefined || field.config.useCustomConditions) && (
        <>
          {showInlineConditions && (
            <Stack sx={{ width: "100%" }}>
              <Divider sx={{ borderStyle: "dashed" }} />
              <MeasureItemConditions
                {...conditionRowsProps}
                autoShowItemOptionsId={popupAnchorEl ? undefined : autoShowItemOptionsId}
              />
            </Stack>
          )}
          {popupAnchorEl && (
            <Popover
              open
              anchorEl={popupAnchorEl}
              onClose={onClosePopup}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              slotProps={{ paper: { sx: { width: 300, maxWidth: "calc(100vw - 32px)" } } }}
            >
              <Stack
                role="dialog"
                aria-label={`Conditions for ${getMeasureCaption(field)}`}
                sx={{
                  "& .field-condition-list": { py: 1 },
                  "& .field-condition-row": { px: 1.5, gap: 1 },
                  "& .condition-marker": { left: 6, top: 4, bottom: 4, height: "auto" },
                }}
              >
                <Stack spacing={0.5} sx={{ px: 2, py: 1.5 }}>
                  <Typography color="textSecondary" variant="caption">
                    Conditions
                  </Typography>
                  <TypographyTooltipEllipsis text={getMeasureCaption(field)} />
                </Stack>
                <Divider />
                {conditionRowsProps.conditions.length > 0 ? (
                  <MeasureItemConditions {...conditionRowsProps} autoShowItemOptionsId={autoShowItemOptionsId} />
                ) : (
                  <Typography color="textSecondary" sx={{ px: 2, py: 1.5 }}>
                    No conditions on this field yet.
                  </Typography>
                )}
                {canBeRemoved && !readonly && (
                  <>
                    <Divider />
                    <Stack sx={{ py: 0.5 }}>
                      <AddCustomConditions
                        dimensions={fieldsForConditions}
                        conditions={conditions}
                        addCondition={addCondition}
                      />
                      <LinkConditions
                        linkableConditions={linkableConditions}
                        disableEqualitySelection
                        setLinkedCondition={handleSetLinkCondition}
                      />
                    </Stack>
                  </>
                )}
              </Stack>
            </Popover>
          )}
        </>
      )}
      {showMeasureOptions && fieldOptions?.ref && currentCondition && (
        <ConditionFieldOptionPopup
          anchorEl={fieldOptions.ref}
          field={currentCondition}
          narrowingFilters={narrowingFilters}
          showParameter={canToggleParameter && !readonly}
          updateParameter={onUpdateAsParameter}
          updateMandatory={onUpdateAsMandatory}
          updateCustomLabel={(label) => {
            onUpdateCustomLabel(currentCondition, label);
            setShowMeasureOptions(false);
          }}
          save={(filter) => {
            updateCustomConditionFilterAndParameter(currentCondition, filter);
            setShowMeasureOptions(false);
          }}
          onRemoveItem={(item) => {
            onRemoveConditionItem(item);
            setShowMeasureOptions(false);
          }}
          canBeRemoved={canBeRemoved}
          resizable={field.meta.type !== ItemDataType.Date}
          disableEqualitySelection={true}
          disableReferenceDateSelection={isApiReport}
          readonlyCustomLabel={isReadonlyCustomLabel}
          CustomId={null}
          readonly={readonly}
        />
      )}
    </>
  );
};

export default CustomConditions;
